import { DatePicker, Input, Message, Toggle } from '@bitcine/cinesend-theme/dist'
import { useEffect, useState } from 'react'
import useStore from '../../hooks/use_store'
import Dropdown from './dropdown'
import ImageField from '../image_field'
import FileSelector from './file_selector'
import { useNavigate, useParams } from 'react-router-dom'
import isEmpty from '../../helpers/is_empty'
import { useQueryClient } from '@tanstack/react-query'
import uuid from 'react-uuid'

const FieldValue = ({
  className = '',
  field,
  assetID,
  cycleID,
  languageID = null,
  isLoading,
  disabled,
  refreshValues,
  cacheKey,
}) => {
  const { updateModel, createModel } = useStore()
  const fieldValue = field.value
  const fieldType = field.field_type
  const [value, setValue] = useState(fieldValue?.value ?? fieldValue?.file_name ?? '')
  const params = useParams()
  const navigate = useNavigate()
  const isDisabled = disabled || field?.is_external_api_value_source
  const queryClient = useQueryClient()

  const issues = (fieldValue?.issues || []).concat(field?.asset_image?.issues || [])
  const hasIssues = issues.length > 0 || (field.is_required && isEmpty(value))
  const fieldName = field.name + (field.is_required ? ' *' : '')
  const issueId = `field-errors-${field.value?.id}`

  useEffect(() => {
    setValue(fieldValue?.value ?? fieldValue?.file_name ?? '')
  }, [fieldValue, field])

  const refreshAsset = () => queryClient.invalidateQueries([`cycles/${cycleID}/assets/${assetID}`])
  const saveField = (newValue) => {
    // save only if value has changed
    if ((isEmpty(fieldValue?.value) && isEmpty(newValue)) || fieldValue?.value === newValue) {
      return
    }

    setValue(newValue)

    // If the field value already exists, we're just updating the value. Otherwise, we create it.
    if (!isEmpty(fieldValue?.value)) {
      updateModel.mutate({
        endpoint: 'field-values',
        id: fieldValue.id,
        data: {
          value: newValue,
          cycle_id: cycleID,
          field_id: field.id,
        },
        onSuccess: (res) => {
          setValue(res?.data?.field?.value)
          // update asset id if changed
          if (res.data.field.asset_id !== assetID) {
            navigate(
              `/airlines/${params.airlineID}/cycles/${cycleID}/metadata/${params.assetType}/${res.data.field.asset_id}/details/${params['*']}`
            )
          } else {
            refreshAsset()
            refreshValues()
          }
        },
        onError: (error) => {
          refreshAsset()
          refreshValues()
        },
      })
    } else {
      let data = {
        field_id: field.id,
        asset_id: parseInt(assetID),
        value: newValue,
        cycle_id: cycleID,
      }

      // Do not send language_id if this is a dropdown
      if (fieldType !== 'dropdown') {
        data['language_id'] = languageID
      }

      createModel.mutate(
        {
          endpoint: 'field-values',
          data,
        },
        {
          onSuccess: (res) => {
            if (res.data.field.asset_id !== assetID) {
              refreshValues()
            }
            setValue(res?.data?.field?.value)
            refreshAsset()
            refreshValues()
          },
          onError: (error) => {
            refreshAsset()
            refreshValues()
          },
        }
      )
    }
  }

  const CopyrightIcon = () => {
    return '©'
  }

  return (
    <div className={`${className} w-full`}>
      {fieldType === 'string' && (
        <div className='flex items-end space-x-2'>
          <Input
            input={fieldName?.toLocaleLowerCase() === 'copyright' ? { leftIcon: { icon: CopyrightIcon } } : null}
            label={fieldName}
            value={value}
            disabled={isDisabled}
            onChange={(e) => setValue(e.target.value)}
            onBlur={(e) => saveField(e.target.value)}
            error={hasIssues}
          />
        </div>
      )}
      {['video', 'audio'].includes(fieldType) && (
        <div className='flex items-end space-x-2'>
          <Input
            label={fieldName}
            value={value}
            disabled={true} // Disable input for video/audio fields. Let the file selector handle it.
            onChange={(e) => setValue(e.target.value)}
            onBlur={(e) => saveField(e.target.value)}
            error={hasIssues}
          />
          <FileSelector
            cycleID={cycleID}
            assetID={assetID}
            value={value}
            field={field}
            isLoading={isLoading}
            saveField={refreshValues}
            refreshAsset={refreshAsset}
          />
        </div>
      )}
      {fieldType === 'datetime' && (
        <DatePicker
          useLocal={false}
          label={fieldName}
          className='w-full'
          date={value}
          disabled={isDisabled}
          showTimeSelect={false}
          onChange={(newDate) => saveField(newDate)}
          error={hasIssues}
        />
      )}
      {fieldType === 'dropdown' && (
        <Dropdown
          cycleID={cycleID}
          field={field}
          label={fieldName}
          value={value}
          disabled={isDisabled}
          isLoading={isLoading}
          languageID={languageID}
          saveField={saveField}
          error={hasIssues}
        />
      )}
      {fieldType === 'image' && (
        <ImageField
          assetID={assetID}
          field={field}
          value={value}
          message={issues[0]?.description}
          saveField={saveField}
          refreshAsset={refreshAsset}
          disabled={isDisabled}
          cacheKey={cacheKey}
        />
      )}
      {fieldType === 'boolean' && (
        <Toggle
          label={fieldName}
          checked={value === 'true'}
          onChange={() => saveField(value === 'true' ? 'false' : 'true')}
        />
      )}
      {fieldType === 'number' && (
        <div className='flex items-end space-x-2'>
          <Input
            label={fieldName}
            type={'number'}
            value={value}
            disabled={isDisabled}
            onChange={(e) => setValue(e.target.value)}
            onBlur={(e) => saveField(e.target.value)}
            error={hasIssues}
            id={`field-value-${field.value?.id}`}
          />
        </div>
      )}
      {hasIssues && issues.length > 0 && (
        <Message error={true} small={true} className='h-auto'>
          <ul className={issues.length > 1 ? 'list-disc list-inside' : 'list-none'} id={issueId}>
            {issues.map((issue) => (
              <li className='' key={issue.id}>
                {issue.description}
              </li>
            ))}
          </ul>
        </Message>
      )}
    </div>
  )
}

export default FieldValue
